// BeforeAfter (TASK-496) — the "values honoured · within CIO" before/after screen, ported from
// SixEase and re-grounded on Kielis. SixEase used a nested positions[].candidates[] + kept[] model;
// Kielis's SwapResponse is FLAT proposals[] (candidate_isin null => no compliant swap = "kept").
// Shows the FULL portfolio composition before/after (sub-asset-class weights — identical by
// mandate-neutrality), the fit impact (before → after), resolved instrument swaps, and conflicts
// with no compliant swap. Grounds on getFit (fit + holdings) + getSwaps + getDrift (composition)
// + getPortfolioOverview (client name). Approve/skip per proposal (HITL, per-proposal endpoints).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, ShieldCheck, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { portfolioFitService, type FitHolding, type SwapProposal } from '../../../services/portfolioFitService';
import { useFit } from '../../../hooks/useFit';
import type { SignalScope } from '../../../types/signalScope';
import TagProvenanceChip from '../../portfolio/TagProvenanceChip';
import { useDrift } from '../../../hooks/useDrift';
import { WidgetContainer } from '../WidgetContainer';

const chfFormat = (v: number) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(v);
const fitPct = (s: number | null) => (s == null ? '—' : `${Math.round(s * 100)}`);
const fitColor = (s: number | null) =>
  s == null ? 'text-muted-foreground' : s === 0 ? 'text-destructive' : s >= 0.75 ? 'text-emerald-600' : 'text-amber-500';

interface CompRow {
  sub_asset_class: string;
  pct: number;
}

interface Loaded {
  clientName: string;
  fitBefore: number | null;
  fitAfter: number | null;
  composition: CompRow[];
  resolved: SwapProposal[]; // candidate_isin present
  kept: SwapProposal[]; // no compliant swap
  holdingsById: Map<string, FitHolding>;
}

// Exposure-weighted mean fit after the resolved swaps apply (mirrors the backend).
function projectedFit(holdings: FitHolding[], gainByPosition: Map<string, number>): number | null {
  let num = 0;
  let den = 0;
  for (const h of holdings) {
    const mv = Number(h.market_value ?? 0);
    if (!mv || h.fit_score == null) continue;
    const gain = gainByPosition.get(h.position_id) ?? 0;
    num += mv * Math.min(1, h.fit_score + gain);
    den += mv;
  }
  return den > 0 ? num / den : null;
}

/**
 * TASK-824: is this the holding the advisor clicked?
 *
 * Matched on ISIN because that is what the Radar's entity key carries for an instrument
 * signal, and what `focusIsin` therefore is. A holding with no ISIN can never be the focus,
 * which is correct: the signal that opened this panel was about a specific instrument.
 */
const isFocused = (held: FitHolding | undefined, focusIsin?: string) =>
  Boolean(focusIsin && held?.isin && held.isin === focusIsin);

/**
 * The focused holding leads its list. Stable otherwise — `Array.prototype.sort` is stable in
 * every engine this ships to, so the server's ordering survives underneath.
 */
function focusFirst(
  proposals: SwapProposal[],
  holdingsById: Map<string, FitHolding>,
  focusIsin?: string,
): SwapProposal[] {
  if (!focusIsin) return proposals;
  return [...proposals].sort((a, b) => {
    const af = isFocused(holdingsById.get(a.position_id), focusIsin) ? 0 : 1;
    const bf = isFocused(holdingsById.get(b.position_id), focusIsin) ? 0 : 1;
    return af - bf;
  });
}

/**
 * TASK-824: the signal identity the Radar has always passed and this widget always dropped.
 *
 * `ChangeRadar` summons Swap with `{clientId, subjectKey, signalGroupKey, originType,
 * originId}` and the component declared `{clientId}` alone — React drops unknown props in
 * silence, so four of five were discarded and the panel opened on whatever came first. Live:
 * launched from an Amazon DNA conflict it opened on Tyson Foods, with Amazon 11th of 11 and
 * unhighlighted.
 *
 * Shaped to TASK-829's `SignalScope` so that ticket widens this rather than rewriting it.
 */
// TASK-829: the props are the shared `SignalScope`, not a bag of this widget's own. Declared
// once so a summon site cannot pass something no widget agreed to receive.
export type BeforeAfterProps = SignalScope;

export function BeforeAfter({ clientId, focusIsin }: BeforeAfterProps) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  // Fit is required (gates error); swaps/drift/overview enrich the view and degrade to null.
  const fitQuery = useFit(clientId);
  const driftQuery = useDrift(clientId);
  const swapsQuery = useQuery({
    queryKey: ['portfolio-swaps', clientId],
    queryFn: () => portfolioFitService.getSwaps(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
  const ovQuery = useQuery({
    queryKey: ['portfolio-overview', clientId],
    queryFn: () => portfolioApiService.getPortfolioOverview(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });

  const loading =
    fitQuery.isLoading || driftQuery.isLoading || swapsQuery.isLoading || ovQuery.isLoading;

  const d: Loaded | null = fitQuery.data
    ? (() => {
        const fit = fitQuery.data;
        const proposals = swapsQuery.data?.proposals ?? [];
        const resolved = proposals.filter((p) => p.candidate_isin);
        const kept = proposals.filter((p) => !p.candidate_isin);
        const gainByPosition = new Map<string, number>();
        for (const p of resolved) if (p.fit_gain != null) gainByPosition.set(p.position_id, p.fit_gain);
        const holdingsById = new Map<string, FitHolding>();
        for (const h of fit.holdings) holdingsById.set(h.position_id, h);
        const composition: CompRow[] = Object.entries(driftQuery.data?.drift?.drift_by_asset_class ?? {})
          .map(([sub, v]) => ({ sub_asset_class: sub, pct: v.current }))
          .sort((a, b) => b.pct - a.pct);
        return {
          clientName: ovQuery.data?.client_name ?? '',
          fitBefore: fit.portfolio_fit,
          fitAfter: projectedFit(fit.holdings, gainByPosition),
          composition,
          resolved,
          kept,
          holdingsById,
        };
      })()
    : null;

  const decide = async (proposalId: string, kind: 'approve' | 'skip') => {
    setPendingId(proposalId);
    try {
      if (kind === 'approve') await portfolioFitService.approveSwap(clientId, proposalId);
      else await portfolioFitService.skipSwap(clientId, proposalId);
      // Refetch the fetches a decision changes; body stays rendered (isLoading stays false).
      await Promise.all([fitQuery.refetch(), swapsQuery.refetch(), driftQuery.refetch()]);
    } catch (err) {
      // e.g. a read-only session 403 — surface it, keep the proposal list usable.
      toast.error(
        `${kind === 'approve' ? 'Approve' : 'Skip'} failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setPendingId(null);
    }
  };

  return (
    <WidgetContainer source="Portfolio · Swaps">
      {fitQuery.isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load the before/after view.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {fitQuery.error instanceof Error ? fitQuery.error.message : String(fitQuery.error)}
          </p>
        </div>
      )}
      {!fitQuery.isError && loading && (
        <div className="space-y-4">
          <div className="h-20 animate-pulse rounded-xl bg-accent" />
          <div className="h-40 animate-pulse rounded-xl bg-accent" />
        </div>
      )}
      {!fitQuery.isError && !loading && d && (
        <Body d={d} pendingId={pendingId} onDecide={decide} focusIsin={focusIsin} />
      )}
    </WidgetContainer>
  );
}

function Body({
  d,
  pendingId,
  onDecide,
  focusIsin,
}: {
  d: Loaded;
  pendingId: string | null;
  onDecide: (id: string, kind: 'approve' | 'skip') => void;
  /** TASK-824: the instrument the signal was about. Leads its list and is highlighted. */
  focusIsin?: string;
}) {
  const totalConflicts = d.resolved.length + d.kept.length;
  if (totalConflicts === 0) {
    return (
      <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
        <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
        No conflicts — every holding already aligns with this client&apos;s values and the CIO list.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Headline — TASK-654: never claim success while conflicts remain
          unresolved. Full success is emerald; anything else is an honest
          attention state naming the remaining work. */}
      <div
        className={
          d.kept.length === 0
            ? 'rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4'
            : 'rounded-xl border border-status-attention/30 bg-status-attention/10 p-4'
        }
      >
        <div className="flex items-start gap-3">
          <ShieldCheck
            className={`mt-0.5 h-6 w-6 shrink-0 ${d.kept.length === 0 ? 'text-emerald-600' : 'text-status-attention-fg'}`}
          />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight text-foreground">
              {d.kept.length === 0
                ? 'Values honoured · 100% within CIO strategy'
                : d.resolved.length > 0
                  ? `${d.resolved.length} conflict${d.resolved.length === 1 ? '' : 's'} resolved · ${d.kept.length} still unresolved`
                  : `${d.kept.length} value conflict${d.kept.length === 1 ? '' : 's'} unresolved`}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {d.kept.length === 0
                ? `${d.clientName || 'Client'} — personalised to this client's values without leaving the CIO strategy.`
                : `${d.clientName || 'Client'} — the remaining conflict${d.kept.length === 1 ? ' has' : 's have'} no compliant same-slot CIO replacement yet; review them below.`}
            </p>
            <p className="mt-2 text-sm text-foreground">
              {d.resolved.length > 0 ? (
                <>
                  Replaced <strong>{d.resolved.length}</strong> holding{d.resolved.length === 1 ? '' : 's'} that
                  conflicted with this client&apos;s values — each with a same-slot CIO-BUY that fits better.
                </>
              ) : (
                <>
                  <strong>{d.kept.length}</strong> conflict{d.kept.length === 1 ? '' : 's'} flagged — no compliant CIO
                  replacement available (same-slot candidates hit the same red line). Shown below.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* TASK-657: recommendation ≠ execution — the destination is explicit. */}
      <p className="text-[11px] text-muted-foreground" data-testid="rebalance-destination">
        This is a recommendation only — no orders are placed. Apply it in a Kielis-managed
        portfolio or export it for the client's PMS/custodian; connected systems are never
        written to from here.
      </p>

      {/* Composition (before/after) + impact */}
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1 rounded-xl border border-border bg-muted/40 p-3">
          <h3 className="text-[13px] font-semibold text-foreground">Strategy unchanged — full composition</h3>
          <p className="mb-3 mt-1 text-[12px] text-muted-foreground">
            Sub-asset-class weights are <strong className="text-foreground">identical before and after</strong> — the
            mandate never moves; only the instrument filling each slot does.
          </p>
          {d.composition.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="pb-2 font-medium">Sub-asset class</th>
                    <th className="pb-2 text-right font-medium">Before</th>
                    <th className="pb-2 text-right font-medium">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {d.composition.map((r) => {
                    const w = `${r.pct.toFixed(1)}%`;
                    return (
                      <tr key={r.sub_asset_class}>
                        <td className="py-1.5 text-foreground">{r.sub_asset_class}</td>
                        <td className="py-1.5 text-right tabular-nums text-muted-foreground">{w}</td>
                        <td className="py-1.5 text-right tabular-nums text-emerald-600">{w}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground/70">
              Allocation breakdown unavailable — weight-neutrality holds by construction (swaps preserve each slot&apos;s
              sub-asset class).
            </p>
          )}
        </div>

        <div className="w-full shrink-0 lg:w-[240px]">
          <div className="rounded-xl border border-border bg-muted/40 p-3">
            <h3 className="mb-3 text-[13px] font-semibold text-foreground">Personalisation impact</h3>
            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Portfolio fit</dt>
                <dd className="text-right">
                  <span className={fitColor(d.fitBefore)}>{fitPct(d.fitBefore)}</span>
                  <ArrowRight className="mx-1.5 inline h-3.5 w-3.5 text-muted-foreground/70" />
                  <span className={`font-semibold ${fitColor(d.fitAfter)}`}>{fitPct(d.fitAfter)}</span>
                </dd>
              </div>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Conflicts resolved</dt>
                <dd className="text-right">
                  <span className="font-semibold text-foreground">{d.resolved.length}</span>
                  {d.kept.length > 0 && <span className="text-muted-foreground/70"> · {d.kept.length} unresolved</span>}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">CIO compliance</dt>
                <dd className="text-right font-semibold text-emerald-600">Every swap = CIO-BUY</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Resolved instrument deltas */}
      {d.resolved.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-foreground">Instrument deltas</h3>
          <div className="space-y-3">
            {focusFirst(d.resolved, d.holdingsById, focusIsin).map((p) => (
              <SwapCard
                key={p.id}
                p={p}
                held={d.holdingsById.get(p.position_id)}
                pendingId={pendingId}
                onDecide={onDecide}
                focused={isFocused(d.holdingsById.get(p.position_id), focusIsin)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Conflicts without a compliant swap */}
      {d.kept.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-foreground">Conflicts without a compliant swap</h3>
          <div className="space-y-3">
            {focusFirst(d.kept, d.holdingsById, focusIsin).map((p) => (
              <KeptCard
                key={p.id}
                p={p}
                held={d.holdingsById.get(p.position_id)}
                pendingId={pendingId}
                onDecide={onDecide}
                focused={isFocused(d.holdingsById.get(p.position_id), focusIsin)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DecideButtons({
  p,
  pendingId,
  onDecide,
}: {
  p: SwapProposal;
  pendingId: string | null;
  onDecide: (id: string, kind: 'approve' | 'skip') => void;
}) {
  const busy = pendingId === p.id;
  const isPending = p.status === 'pending';
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onDecide(p.id, 'approve')}
        disabled={!isPending || busy}
        title="Approve"
        className="inline-flex h-8 w-8 items-center justify-center rounded border border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onDecide(p.id, 'skip')}
        disabled={!isPending || busy}
        title="Skip"
        className="inline-flex h-8 w-8 items-center justify-center rounded border border-border text-muted-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <SkipForward className="h-4 w-4" />
      </button>
    </div>
  );
}

function SwapCard({
  p,
  held,
  pendingId,
  onDecide,
  focused = false,
}: {
  p: SwapProposal;
  held?: FitHolding;
  pendingId: string | null;
  onDecide: (id: string, kind: 'approve' | 'skip') => void;
  /** TASK-824: this is the instrument the advisor clicked — say so, visibly. */
  focused?: boolean;
}) {
  return (
    <div
      data-testid={focused ? 'swap-focused-holding' : undefined}
      className={`rounded-lg border p-3 ${
        focused ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-muted/40'
      }`}
    >
      {focused && (
        <span className="mb-1.5 inline-block rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
          This signal
        </span>
      )}
      <div className="flex items-stretch gap-2">
        <div className="flex-1 rounded-md border border-destructive/20 bg-destructive/5 p-2.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">Current</p>
          <p className="text-[13px] font-semibold leading-snug text-foreground">{held?.security_name ?? '—'}</p>
          <p className="text-[11px] text-muted-foreground">{held?.isin ?? '—'}</p>
          {held?.market_value != null && (
            <p className="mt-1 text-[11px] text-muted-foreground">{chfFormat(Number(held.market_value))}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center text-muted-foreground/70">
          <ArrowRight className="h-5 w-5" />
        </div>
        <div className="flex-1 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">Swapped</p>
          <p className="text-[13px] font-semibold leading-snug text-foreground">{p.candidate_isin ?? '—'}</p>
          {p.candidate_valor && <p className="text-[11px] text-muted-foreground">Valor {p.candidate_valor}</p>}
          {p.fit_gain != null && <p className="mt-1 font-mono text-[11px] text-emerald-600">+{Math.round(p.fit_gain * 100)}pp fit</p>}
        </div>
      </div>
      <div className="mt-2.5 space-y-1 rounded-md border border-border bg-accent px-2.5 py-2">
        {/* TASK-686: `swap_proposals.mandate_neutral` is written as the literal TRUE by
            compute_swaps — it is true BY CONSTRUCTION (the candidate SQL only ever offers a
            same sub-asset-class, same industry-group replacement, so no slot weight moves),
            not measured. The chip now says which, so it cannot be read as a drift check that
            was run. The LLM proposal path DOES measure drift — see VerifiedConstraints. */}
        {p.mandate_neutral && (
          <span
            title="Same sub-asset-class and industry group, so no slot weight moves. Neutral by construction — not a measured drift figure."
            className="rounded border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
          >
            Mandate neutral · same slot
          </span>
        )}
        {p.dna_reason && <p className="text-[11px] text-muted-foreground"><strong>DNA:</strong> {p.dna_reason}</p>}
        {p.cio_view && <p className="text-[11px] text-muted-foreground"><strong>CIO:</strong> BUY · {p.cio_view}</p>}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground/70">{p.status}</span>
        <DecideButtons p={p} pendingId={pendingId} onDecide={onDecide} />
      </div>
    </div>
  );
}

function KeptCard({
  p,
  held,
  pendingId,
  onDecide,
  focused = false,
}: {
  p: SwapProposal;
  held?: FitHolding;
  pendingId: string | null;
  onDecide: (id: string, kind: 'approve' | 'skip') => void;
  /** TASK-824: this is the instrument the advisor clicked — say so, visibly. */
  focused?: boolean;
}) {
  const tags = (held?.conflicts ?? []).filter((c) => c.impact === 'exclusion').map((c) => c.tag);
  return (
    <div
      data-testid={focused ? 'swap-focused-holding' : undefined}
      className={`rounded-lg border p-3 ${
        focused ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-muted/40'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-2">
        {focused && (
          <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
            This signal
          </span>
        )}
        <span className="rounded border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600">
          No compliant swap
        </span>
        <span className="text-[13px] font-semibold text-foreground">{held?.security_name ?? '—'}</span>
      </div>
      {/* TASK-827: the tag AND its basis. "No compliant swap for Tyson Foods because it is a
          luxury conglomerate" is a sentence an advisor repeats to a client; it rests on a
          default applied to every holding in Consumer Staples. */}
      <div className="mb-1.5 flex flex-wrap gap-1">
        {tags.map((tag) => (
          <TagProvenanceChip key={tag} tag={tag} provenance={held?.tag_provenance?.[tag]} />
        ))}
      </div>
      {p.dna_reason && <p className="text-[11px] text-muted-foreground">{p.dna_reason}</p>}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground/70">{p.status}</span>
        <DecideButtons p={p} pendingId={pendingId} onDecide={onDecide} />
      </div>
    </div>
  );
}
